import {
  CompanyProfileLayoutV2_organization$data,
  type CompanyProfileLayoutV2_organization$key,
} from '#app/__generated__/relay/CompanyProfileLayoutV2_organization.graphql.js';
import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { LinkBanner } from '#app/components/Banners/LinkBanner.js';
import { Button } from '#app/components/Buttons/Button.js';
import { CompanyProfileMainContentV2 } from '#app/components/companyProfilesV2/components/CompanyProfileMainContentV2/CompanyProfileMainContentV2.js';
import { CompanyProfileMenuV2 } from '#app/components/companyProfilesV2/components/CompanyProfileMenuV2/CompanyProfileMenuV2.js';
import { EditableCompanyLogoV2 } from '#app/components/companyProfilesV2/components/EditableCompanyLogo/EditableCompanyLogoV2.js';
import { EssentialInfoV2 } from '#app/components/companyProfilesV2/components/EssentialInfoV2/EssentialInfoV2.js';
import { InlineDescriptionV2 } from '#app/components/companyProfilesV2/components/InlineDescriptionV2/InlineDescriptionV2.js';
import { GridColumn } from '#app/components/Grid/GridColumn.js';
import { Link } from '#app/components/Link/index.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { ProfileBadgeTag } from '#app/components/ProfileBadgeTag/ProfileBadgeTag.js';
import { SocialMediaLinkWithTooltip } from '#app/components/SocialMediaLink/SocialMediaLinkWithTooltip.js';
import { AdminActionsForCompany } from '#app/features/profile/components/AdminActionsForCompany/AdminActionsForCompany.js';
import { useAddUserInterfaceFlag } from '#app/hooks/useAddUserInterfaceFlag.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { useHasUserFeatures } from '#app/hooks/useHasUserFeatures.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { useMembersOnlyBadge } from '#app/hooks/useMembersOnlyBadge.js';
import { MainLayout } from '#app/layouts/MainLayout/MainLayout.js';
import { theme } from '#app/theme/theme.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import {
  Banner,
  BannerActions,
  Column,
  CompanyDescription,
  GridLayout,
  HeaderContainer,
  LogoAndName,
  SectionText,
  ShareButton,
} from './CompanyProfileLayout.styles.js';
import {
  CalendarIcon,
  ChevronRightIcon,
  EditIcon,
  MembersOnlyIcon,
  RecommendationStarFullIcon,
  ShareAltIcon,
  TagIcon,
  VerifiedGreenGradientIcon,
} from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { Variants } from 'framer-motion';
import { Fragment, Suspense, useCallback, useMemo, useState } from 'react';
import { css, styled, useTheme } from 'styled-components';

type ReviewNode = NonNullable<
  CompanyProfileLayoutV2_organization$data['userReviews']['edges']
>[number]['node'];

const viewMoreVariants: Variants = {
  close: {
    height: 0,
    scaleY: 0,
    transition: {
      duration: 0.1,
    },
  },
  open: {
    height: 'auto',
    scaleY: 1,
    transition: {
      duration: 0.1,
    },
  },
};

const RecommendationContainer = styled(Link)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const Recommendations = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.15px;
  color: ${theme.colorsV2.black};
  &:hover {
    cursor: pointer;
  }
`;

const RoundButton = styled.button`
  ${({ theme: styledTheme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 20px;
    color: ${styledTheme.colorsV2.black};
    background-color: ${styledTheme.colorsV2.white};

    svg {
      width: 16px;
      height: 16px;
    }

    border: 1px solid ${styledTheme.colorsV2.gray40};

    &:hover,
    &:focus,
    &:active {
      background-color: ${styledTheme.colorsV2.white};
    }
  `}
`;

const Flex = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;

  ${theme.mediaQueries.md} {
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
  }
`;

const Links = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 4px;
  align-items: center;

  & a,
  button {
    border: none;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: ${theme.colorsV2.gray50};
  display: none;

  ${theme.mediaQueries.md} {
    display: block;
  }
`;

const Left = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;

  ${theme.mediaQueries.md} {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

export const CompanyProfileLayoutV2 = ({
  organizationRef,
}: {
  readonly organizationRef: CompanyProfileLayoutV2_organization$key;
}) => {
  const { track } = useAnalytics();
  const copyToClipboard = useCopyToClipboard();
  const data = useFragment<CompanyProfileLayoutV2_organization$key>(
    graphql`
      fragment CompanyProfileLayoutV2_organization on Organization {
        id
        slug
        isClaimed
        name
        isVerified
        id
        url
        description
        externalLinks {
          edges {
            node {
              url
              label
              id
            }
          }
        }
        numberOfEmployees
        location
        visitorCanEdit
        visitorIsMember
        industryCategories {
          edges {
            node {
              id
              name
            }
          }
        }
        jobs(filter: { statuses: [POSTED] }) {
          count
        }
        userReviews {
          count
          edges {
            node {
              author {
                id
                firstName
                avatarImage {
                  url
                  id
                  uid
                }
              }
            }
          }
        }
        yearFounded

        logoImage {
          dominantHexColor
        }
        workspace {
          ... on ClientWorkspace {
            activeSubscription {
              createdAt
            }
          }
        }

        ...CompanyProfileMenuV2_organization
        ...CompanyProfileMainContentV2_organization
        ...EditableCompanyLogoV2_organization
        ...EssentialInfoV2_organization
        ...InlineDescriptionV2_organization
        ...WelcomeToYourCompanyProfileModal_organization
      }
    `,
    organizationRef,
  );

  const hasPermissions = useHasUserFeatures(['RATE_USERS']);

  const styledTheme = useTheme();
  const isMediumScreen = useMatchMedia(
    parseMediaQueryForMatchMedia(styledTheme.mediaQueries.md),
  );
  const [isViewingMore, setIsViewingMore] = useState<boolean>(false);
  const currentUser = useCurrentUser();

  const hasSeenWelcomeToYourCompanyProfilePreviously = useMemo(() => {
    const uiFlags = currentUser?.userInterfaceFlags ?? [];
    return uiFlags.includes('SEEN_WELCOME_TO_YOUR_COMPANY_PROFILE_MODAL');
  }, [currentUser?.userInterfaceFlags]);

  const { isClaimed, slug, visitorIsMember } = data;

  const onShareClick = useCallback(async () => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_share_company_profile',
    });

    if (slug)
      await copyToClipboard(
        'https://contra.com' + getRoute('/company/@slug', { args: { slug } }),
        {
          successToast: 'The link was successfully copied to your clipboard',
        },
      );
  }, [copyToClipboard, slug, track]);

  const [addUserInterfaceFlag] = useAddUserInterfaceFlag();

  const requestToJoinBanner = (
    <LinkBanner
      backgroundColor={theme.colorsV2.blue10}
      buttonSize="small"
      buttonVariant="V2Primary"
      centerContent
      content="This company is claimed. Looking to manage it?"
      fullBleed
      hideButtonIcon
      hideIcon
      href={currentUser ? undefined : getRoute('/sign-up')}
      isOpen
      linkLabel="Request to join"
      textCustomStyles={{
        color: theme.colorsV2.black,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '24px',
      }}
      useButton
    />
  );

  const claimCompanyBanner = (
    <LinkBanner
      backgroundColor={theme.colorsV2.blue10}
      buttonSize="small"
      buttonVariant="V2Primary"
      centerContent
      content="Looking to manage this company profile?"
      fullBleed
      hideButtonIcon
      hideIcon
      href={currentUser ? undefined : getRoute('/sign-up')}
      isOpen
      linkLabel="Claim this company profile"
      textCustomStyles={{
        color: theme.colorsV2.black,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '24px',
      }}
      useButton
    />
  );

  const onClickViewMore = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'company_profile_view_more_details',
    });

    setIsViewingMore(true);
  }, [track]);

  const maybeCompanyBanner = () => {
    if (!currentUser && !isClaimed) return claimCompanyBanner;
    if (!currentUser && isClaimed) return requestToJoinBanner;
    return undefined;
  };

  const { openModal: openEditOrganizationModal } = useModal({
    identifier: 'EditOrganizationModal',
    modalProps: {
      defaultValues: {
        description: data?.description,
        externalLinks: data?.externalLinks,
        id: data.id,
        industryCategories: data?.industryCategories,
        location: data?.location,
        name: data.name,
        numberOfEmployees: data?.numberOfEmployees,
        url: data?.url,
        yearFounded: data?.yearFounded,
      },
      organizationId: data.id,
    },
  });

  const { openModal: openWelcomeToYourCompanyProfileModal } = useModal({
    identifier: 'WelcomeToYourCompanyProfileModal',
    modalProps: {
      onModalClose: () => {
        addUserInterfaceFlag({
          variables: {
            input: {
              userInterfaceFlag: 'SEEN_WELCOME_TO_YOUR_COMPANY_PROFILE_MODAL',
            },
          },
        });
      },
      organizationRef: data,
    },
  });

  useMount(() => {
    if (!hasSeenWelcomeToYourCompanyProfilePreviously && visitorIsMember) {
      openWelcomeToYourCompanyProfileModal();
    }
  });

  const recommendationsText = data.userReviews?.count
    ? `Recommended ${data.userReviews?.count}x`
    : null;

  const shouldHideViewMore =
    !data.industryCategories.edges.length && !data.yearFounded;

  const onEditCompany = useCallback(() => {
    openEditOrganizationModal({});
  }, [openEditOrganizationModal]);

  const uniqueAuthors = data.userReviews.edges.reduce(
    (accumulator, { node: currentNode }) => {
      const currentAuthor = currentNode.author.id;
      if (!accumulator.has(currentAuthor)) {
        accumulator.set(currentAuthor, currentNode);
      }

      return accumulator;
    },
    new Map<string, ReviewNode>(),
  );
  const uniqueAuthorsArray = Array.from(uniqueAuthors.values());

  const organizationLinks = useMemo(
    () =>
      [
        {
          component: (
            <Button
              aria-label="edit company details"
              icon={EditIcon}
              iconPlacement="left"
              onClick={onEditCompany}
              removeMinWidth
              size="small"
              variant="V2TertiaryOutline"
            >
              Edit Company Details
            </Button>
          ),
          id: 'COMPANY_DETAILS_EDIT',
          shouldRender: data.visitorCanEdit && !isMediumScreen,
        },
        {
          component: (
            <Button
              as={Link}
              icon={ChevronRightIcon}
              iconPlacement="right"
              removeMinWidth
              size="small"
              to={getRoute('/company/@slug/jobs', {
                args: { slug: data.slug ?? '' },
              })}
            >
              View active jobs ({data.jobs.count})
            </Button>
          ),
          id: 'VIEW_ACTIVE_JOBS',
          shouldRender: data.jobs.count > 0,
        },
        {
          component: (
            <RecommendationContainer
              to={getRoute('/company/@slug/recommendations', {
                args: { slug: slug ?? '' },
              })}
            >
              <RecommendationStarFullIcon size={16} />
              <Recommendations>{recommendationsText}</Recommendations>
              <AvatarGroup
                data={uniqueAuthorsArray.map((node) => ({
                  avatarImage: node.author.avatarImage,
                  fallbackName: node.author.firstName ?? undefined,
                  key: node.author.id,
                }))}
                dataCount={data.userReviews.count}
                maxCount={3}
                size="xs"
              />
            </RecommendationContainer>
          ),
          id: 'RECOMMENDATIONS',

          shouldRender: data.userReviews.count > 0,
        },
        {
          component: (
            <Links>
              {data.externalLinks.edges.map((edge, index) => (
                <SocialMediaLinkWithTooltip
                  key={edge.node.id}
                  label={edge.node.label}
                  position={index}
                  url={edge.node.url}
                  visitorCanEdit={false}
                />
              ))}
            </Links>
          ),
          id: 'EXTERNAL_LINKS',
          shouldRender: data.externalLinks.edges.length > 0,
        },
      ].filter((link) => link.shouldRender),
    [
      data.externalLinks.edges,
      data.jobs.count,
      data.slug,
      data.userReviews.count,
      data.visitorCanEdit,
      isMediumScreen,
      onEditCompany,
      recommendationsText,
      slug,
      uniqueAuthorsArray,
    ],
  );

  const membersOnly = useFeatureFlag('membersOnly');

  const { memberSinceLabel, memberSinceTooltip } = useMembersOnlyBadge({
    firstName: null,
    organizationName: data.name,
    subscriptionCreatedAt:
      data?.workspace?.activeSubscription?.createdAt ?? null,
  });

  const memberSinceBadge = membersOnly ? (
    memberSinceLabel ? (
      <ProfileBadgeTag
        Icon={MembersOnlyIcon}
        label={memberSinceLabel}
        size="small"
        tooltip={memberSinceTooltip ?? undefined}
        variant="purple-gradient"
      />
    ) : null
  ) : data.isVerified ? (
    <VerifiedGreenGradientIcon />
  ) : null;

  return (
    <MainLayout
      backgroundColor="white"
      banner={maybeCompanyBanner()}
      hideAppBarBorderAtMd
      noMarginOnTheTop
      routeLabelOverride={{ label: data.name }}
    >
      <Banner $companyColor={data.logoImage?.dominantHexColor ?? '#06BCCD'}>
        <BannerActions>
          <CompanyProfileMenuV2 organizationRef={data} />
          <ShareButton
            icon={ShareAltIcon}
            onClick={onShareClick}
            removeMinWidth
            size={isMediumScreen ? 'xsmall' : 'small'}
            variant="V2TertiaryOutline"
          >
            Share
          </ShareButton>
        </BannerActions>
      </Banner>
      <GridLayout>
        <GridColumn
          center
          initial={12}
          lg={9}
        >
          <HeaderContainer>
            <LogoAndName>
              <EditableCompanyLogoV2 organizationRef={data} />
              <EssentialInfoV2
                memberSinceBadge={
                  isMediumScreen || !membersOnly ? memberSinceBadge : null
                }
                organizationRef={data}
              />
            </LogoAndName>
            {isMediumScreen || !membersOnly ? null : memberSinceBadge}
            <CompanyDescription>
              <div>
                <InlineDescriptionV2
                  hideViewMore={shouldHideViewMore}
                  isViewingMore={isViewingMore}
                  onClickViewMore={onClickViewMore}
                  organizationRef={data}
                />
              </div>

              {data.visitorCanEdit && isMediumScreen ? (
                <RoundButton
                  aria-label="edit company details"
                  onClick={onEditCompany}
                >
                  <EditIcon />
                </RoundButton>
              ) : null}
            </CompanyDescription>

            <Column
              animate={isViewingMore ? 'open' : 'close'}
              initial="close"
              variants={viewMoreVariants}
            >
              {data.industryCategories.edges.length > 0 ? (
                <SectionText>
                  <TagIcon size={16} />
                  <span>
                    {data.industryCategories.edges
                      .map((edge) => edge.node.name)
                      .join(', ')}
                  </span>
                </SectionText>
              ) : null}

              {data.yearFounded ? (
                <SectionText>
                  <CalendarIcon size={16} />
                  Founded <span>{data.yearFounded}</span>
                </SectionText>
              ) : null}
            </Column>

            <Flex>
              <Left>
                {organizationLinks.map((link, index) => (
                  <Fragment key={link.id}>
                    {link.component}
                    {index === organizationLinks.length - 1 ? null : (
                      <Divider />
                    )}
                  </Fragment>
                ))}
              </Left>
            </Flex>
          </HeaderContainer>
          <CompanyProfileMainContentV2 organizationRef={data} />

          {hasPermissions && slug ? (
            <Suspense fallback={<span>...</span>}>
              <AdminActionsForCompany companySlug={slug} />
            </Suspense>
          ) : null}
        </GridColumn>
      </GridLayout>
    </MainLayout>
  );
};
